import { Button, Col, Row, Tooltip } from "antd";
import { FunctionComponent, ReactNode } from "react";
import { SelectFormItem } from "./SelectFormItem";
import { DatePickerFormItem } from "./DatePickerFormItem";
import { InputFormItem } from "./InputFormItem";
import { DefaultOptionType } from "antd/es/select";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { InputNumberFormItem } from "./InputNumberFormItem";

type InputFilterProps = {
    name: string;
    libelle: string;
    inputType: "Select" | "SelectTags" | "Date" | "InputText" | "InputNumber";
    selectOptions?: DefaultOptionType[];
    doSearch?: () => void;
};

/**
 * Composant de champ de filtre individuel.
 * Rendu en `labelCol` / `wrapperCol` en mode colonne (label au-dessus)
 * pour un meilleur rendu dans la grille responsive.
 */
const InputFilter: FunctionComponent<InputFilterProps> = ({
    name,
    libelle,
    inputType,
    selectOptions,
    doSearch,
    ...rest
}) => {
    /**
     * Props communes pour tous les FormItem dans la grille :
     * label au-dessus de l'input (layout vertical = pas de labelCol/wrapperCol).
     * La taille est normalisée à 100% de la colonne parente.
     */
    const sharedProps = {
        name,
        label: libelle,
        placeholder: libelle,
        style: { width: "100%" },
        ...rest,
    };

    switch (inputType) {
        case "Select":
            return (
                <SelectFormItem
                    {...sharedProps}
                    options={selectOptions}
                    key={name}
                    allowClear
                />
            );
        case "SelectTags":
            return (
                <SelectFormItem
                    {...sharedProps}
                    options={selectOptions}
                    key={name}
                    mode="tags"
                    allowClear
                />
            );
        case "Date":
            return (
                <DatePickerFormItem
                    onChange={doSearch}
                    {...sharedProps}
                    key={name}
                />
            );
        case "InputText":
            return (
                <InputFormItem
                    onPressEnter={doSearch}
                    {...sharedProps}
                    key={name}
                />
            );
        case "InputNumber":
            return (
                <InputNumberFormItem
                    onPressEnter={doSearch}
                    name={name}
                    label={libelle}
                    key={name}
                    {...rest}
                />
            );
        default:
            return <></>;
    }
};

export type InputSearchFieldDef = InputFilterProps & {
    tooltip?: string;
};

export type AdminSearchFilterProps = {
    doSearch: () => void;
    inputFilters: InputSearchFieldDef[];
    /** Noms des filtres que l'utilisateur peut retirer (affiche une croix). */
    removableFilterNames?: string[];
    /** Callback de retrait d'un filtre optionnel. */
    onRemoveFilter?: (name: string) => void;
    /** Contenu additionnel rendu dans la barre d'action (ex. « + Ajouter un filtre »). */
    extraActions?: ReactNode;
};

/**
 * AdminSearchFilter — liste verticale de filtres (un par ligne, pleine largeur),
 * adaptée aux conteneurs étroits (ex. le panneau latéral de 400px des inscriptions).
 *
 * - Conteneur (Card, bordures) géré par le parent, pas ici (évite la double imbrication).
 * - Bouton "Rechercher" aligné à droite ; un slot `extraActions` optionnel (ex.
 *   « + Ajouter un filtre ») est rendu à gauche de ce bouton.
 * - Filtres optionnels retirables via `removableFilterNames` / `onRemoveFilter`.
 * - Les tooltips sont préservés sur les champs qui les ont définis.
 */
export const AdminSearchFilter: FunctionComponent<AdminSearchFilterProps> = ({
    inputFilters,
    doSearch,
    removableFilterNames,
    onRemoveFilter,
    extraActions,
}) => {
    const renderField = (inputFilter: InputSearchFieldDef) => {
        const { name, libelle, inputType, tooltip, selectOptions } = inputFilter;
        const field = (
            <InputFilter
                doSearch={doSearch}
                key={name + inputType}
                name={name}
                libelle={libelle}
                inputType={inputType}
                selectOptions={selectOptions}
            />
        );

        const wrapped = tooltip ? (
            <Tooltip title={tooltip} color="geekblue">
                {field}
            </Tooltip>
        ) : (
            field
        );

        // Filtre optionnel retirable : croix à droite du champ pour le retirer.
        if (removableFilterNames?.includes(name)) {
            return (
                <div key={name} style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>{wrapped}</div>
                    <Tooltip title="Retirer ce filtre">
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => onRemoveFilter?.(name)}
                            style={{ marginTop: 4, color: "#8c8c8c" }}
                            {...({ testid: `retirer-filtre-${name}` } as any)}
                        />
                    </Tooltip>
                </div>
            );
        }

        return <div key={name}>{wrapped}</div>;
    };

    return (
        <>
            <Row gutter={[16, 4]}>
                {inputFilters.map((filter) => (
                    <Col key={filter.name} span={24}>
                        {renderField(filter)}
                    </Col>
                ))}
            </Row>

            {/* Barre d'action : « Ajouter un filtre » à gauche (optionnel), Rechercher à droite */}
            <Row justify={extraActions ? "space-between" : "end"} align="middle" style={{ marginTop: 8 }} gutter={[8, 8]}>
                {extraActions && <Col>{extraActions}</Col>}
                <Col>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={doSearch}
                        style={{ minWidth: 140 }}
                        {...({ testid: "rechercher" } as any)}
                    >
                        Rechercher
                    </Button>
                </Col>
            </Row>
        </>
    );
};